import React, { useState } from "react";
import { DollarSign, Heart, Building2, Loader2 } from "lucide-react";
import { createOneTimeDonation, createMonthlyDonation } from "../../utils/stripe";

function DonatePage() {
    const [donationAmount, setDonationAmount] = useState("");
    const [customAmount, setCustomAmount] = useState("");
    const [donationType, setDonationType] = useState("one-time");
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);

    const presetAmounts = [25, 50, 100, 250, 500];

    const handlePresetClick = (amount) => {
      setDonationAmount(amount.toString());
      setCustomAmount("");
    };

    const handleCustomAmountChange = (e) => {
      setCustomAmount(e.target.value);
      setDonationAmount(e.target.value);
    };

    const handleDonation = async () => {
      setError(null);
      setIsProcessing(true);

      try {
        const amount = parseFloat(donationAmount);

        if (!amount || amount <= 0) {
          throw new Error("Please enter a valid donation amount");
        }

        // Call the appropriate Stripe function based on donation type
        if (donationType === "monthly") {
          await createMonthlyDonation(amount);
        } else {
          await createOneTimeDonation(amount);
        }

        // If we reach here, user cancelled or there was an error
        setIsProcessing(false);
      } catch (err) {
        console.error("Donation error:", err);
        setError(err.message || "An error occurred. Please try again.");
        setIsProcessing(false);
      }
    };

    const getImpactText = (amount) => {
      const numAmount = parseFloat(amount);
      if (!numAmount || numAmount <= 0)
        return "Select an amount to see your impact";

      if (numAmount >= 1000)
        return "Sponsors a full scholarship for one student";
      if (numAmount >= 500) return "Funds a month-long community project";
      if (numAmount >= 250) return "Supports a community workshop";
      if (numAmount >= 100) return "Sponsors a scholarship application review";
      if (numAmount >= 50) return "Funds a week of educational programs";
      if (numAmount >= 25) return "Provides school supplies for 1 student";
      return "Makes a meaningful difference in our community";
    };

  return (
    <div className="donation-section">
      <style jsx>{`
        .donation-section {
          min-height: 100vh;
          background: linear-gradient(
              135deg,
              rgba(46, 49, 60, 0.95) 0%,
              rgba(0, 117, 154, 0.9) 50%,
              rgba(46, 49, 60, 0.95) 100%
            ),
            url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800"><defs><pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1"/></pattern></defs><rect width="100%" height="100%" fill="url(%23grid)"/></svg>');
          background-size: cover;
          background-position: center;
          padding: 4rem 2rem;
          position: relative;
          overflow: hidden;
          margin-bottom: 2rem;
        }

        .donation-section::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(
              circle at 20% 20%,
              rgba(0, 117, 154, 0.2) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 80%,
              rgba(46, 49, 60, 0.3) 0%,
              transparent 50%
            );
          pointer-events: none;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 1rem;
          letter-spacing: -0.02em;
          text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          animation: fadeInUp 1s ease-out;
        }

        .title-underline {
          width: 100px;
          height: 4px;
          background: linear-gradient(90deg, #00759a, #006d8a);
          margin: 0 auto 2rem;
          border-radius: 2px;
          animation: fadeInUp 1s ease-out 0.2s both;
        }

        .donation-section .section-header .section-subtitle {
          font-size: clamp(1.1rem, 2vw, 1.3rem);
          color: rgba(255, 255, 255, 0.9);
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          animation: fadeInUp 1s ease-out 0.4s both;
        }

        .donation-content {
          display: flex;
          flex-direction: column;
          gap: 4rem;
        }

        .donation-form {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 24px;
          padding: 3rem;
          animation: fadeInUp 1s ease-out 0.6s both;
          max-width: 600px;
          margin: 0 auto;
          width: 100%;
        }

        .form-section {
          margin-bottom: 2.5rem;
        }

        .form-section h3 {
          color: #ffffff;
          font-size: 1.3rem;
          font-weight: 600;
          margin-bottom: 1rem;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
        }

        .donation-type-toggle {
          display: flex;
          background: rgba(46, 49, 60, 0.3);
          border-radius: 12px;
          padding: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .toggle-option {
          flex: 1;
          padding: 0.8rem 1rem;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: rgba(255, 255, 255, 0.7);
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .toggle-option.active {
          background: linear-gradient(135deg, #00759a, #006d8a);
          color: #ffffff;
          box-shadow: 0 4px 15px rgba(0, 117, 154, 0.3);
        }

        .preset-amounts {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .preset-button {
          padding: 1rem;
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          color: rgba(255, 255, 255, 0.9);
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .preset-button:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
        }

        .preset-button.active {
          background: linear-gradient(135deg, #00759a, #006d8a);
          border-color: #00759a;
          color: #ffffff;
          box-shadow: 0 4px 15px rgba(0, 117, 154, 0.3);
        }

        .amount-input-container {
          margin-bottom: 1.5rem;
        }

        .custom-amount-label {
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
          display: block;
          font-weight: 500;
        }

        .amount-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .dollar-icon {
          position: absolute;
          left: 1.2rem;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.7);
          z-index: 1;
        }

        .amount-input {
          width: 100%;
          padding: 1.2rem 1.2rem 1.2rem 3.5rem;
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          color: #ffffff;
          font-size: 1.1rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .amount-input::placeholder {
          color: rgba(255, 255, 255, 0.5);
          font-weight: 400;
        }

        .amount-input:focus {
          outline: none;
          border-color: #00759a;
          box-shadow: 0 0 0 3px rgba(0, 117, 154, 0.2);
          background: rgba(255, 255, 255, 0.15);
        }

        .amount-input:hover {
          border-color: rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.12);
        }

        .impact-text {
          background: rgba(0, 117, 154, 0.2);
          border: 1px solid rgba(0, 117, 154, 0.3);
          border-radius: 12px;
          padding: 1rem;
          color: #ffffff;
          font-style: italic;
          text-align: center;
          margin-bottom: 1.5rem;
          min-height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .error-message {
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.4);
          border-radius: 12px;
          padding: 1rem;
          color: #ffffff;
          text-align: center;
          margin-bottom: 1.5rem;
          font-size: 0.95rem;
        }

        .donation-button {
          width: 100%;
          padding: 1.2rem;
          background: linear-gradient(135deg, #16a34a, #15803d);
          color: #ffffff;
          border: none;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .donation-button::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.2),
            transparent
          );
          transition: left 0.5s ease;
        }

        .donation-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(22, 163, 74, 0.4);
          background: linear-gradient(135deg, #15803d, #166534);
        }

        .donation-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .donation-button:disabled:hover {
          transform: none;
          box-shadow: none;
          background: linear-gradient(135deg, #16a34a, #15803d);
        }

        .donation-button:disabled::before {
          display: none;
        }

        .corporate-note {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          margin-top: 3rem;
          animation: fadeInUp 1s ease-out 0.8s both;
        }

        .corporate-note-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .corporate-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, rgba(0, 117, 154, 0.3), rgba(0, 109, 138, 0.3));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid rgba(0, 117, 154, 0.4);
        }

        .corporate-note h3 {
          color: #ffffff;
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0;
        }

        .corporate-note p {
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.6;
          margin: 0.5rem 0 1rem;
          font-size: 0.95rem;
        }

        .contact-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: #ffffff;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .contact-link:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 640px) {
          .donation-section {
            padding: 2rem 1rem;
          }

          .donation-form {
            padding: 2rem 1.5rem;
          }

          .preset-amounts {
            grid-template-columns: repeat(2, 1fr);
          }

          .preset-button {
            font-size: 1rem;
            padding: 0.9rem;
          }

          .amount-input {
            font-size: 1rem;
            padding: 1.1rem 1.1rem 1.1rem 3.2rem;
          }

          .dollar-icon {
            left: 1rem;
          }

          .donation-content {
            gap: 2rem;
          }

          .corporate-note {
            padding: 1.5rem;
            margin-top: 2rem;
          }
        }
      `}</style>

      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Support Our Mission</h2>
          <div className="title-underline"></div>
          <p className="section-subtitle">
            Your contribution helps us empower communities through education and
            collaborative initiatives, creating lasting positive impact for
            generations to come.
          </p>
        </div>

        <div className="donation-content">
          <div className="donation-form">
            <div className="form-section">
              <h3>Donation Type</h3>
              <div className="donation-type-toggle">
                <button
                  className={`toggle-option ${
                    donationType === "one-time" ? "active" : ""
                  }`}
                  onClick={() => setDonationType("one-time")}
                >
                  One-Time
                </button>
                <button
                  className={`toggle-option ${
                    donationType === "monthly" ? "active" : ""
                  }`}
                  onClick={() => setDonationType("monthly")}
                >
                  Monthly
                </button>
              </div>
            </div>

            <div className="form-section">
              <h3>Donation Amount</h3>
              <div className="preset-amounts">
                {presetAmounts.map((amount) => (
                  <button
                    key={amount}
                    className={`preset-button ${
                      donationAmount === amount.toString() && !customAmount ? "active" : ""
                    }`}
                    onClick={() => handlePresetClick(amount)}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
              <div className="amount-input-container">
                <label className="custom-amount-label">Or enter a custom amount</label>
                <div className="amount-input-wrapper">
                  <DollarSign size={24} className="dollar-icon" />
                  <input
                    type="number"
                    placeholder="Enter custom amount"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    className="amount-input"
                    min="1"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            <div className="impact-text">{getImpactText(donationAmount)}</div>

            {error && <div className="error-message">{error}</div>}

            <button
              className="donation-button"
              onClick={handleDonation}
              disabled={!donationAmount || parseFloat(donationAmount) <= 0 || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
                  <span style={{ marginLeft: "0.5rem" }}>Processing...</span>
                </>
              ) : (
                `Donate ${donationType === "monthly" ? "Monthly" : "Now"}`
              )}
            </button>
          </div>

          <div className="corporate-note">
            <div className="corporate-note-content">
              <div className="corporate-icon">
                <Building2 size={28} color="#ffffff" />
              </div>
              <h3>Interested in Corporate Sponsorship?</h3>
              <p>
                We offer tiered sponsorship packages for organizations looking to make a larger impact and align with our mission.
              </p>
              <a href="#contact" className="contact-link">
                <Heart size={18} />
                Contact Us About Partnerships
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DonatePage;
